import React, { ChangeEvent, FC, useEffect, useRef, useState } from 'react';

import { FieldProps } from 'formik';
import IconSvg from '../../IconSvg';
import { debounce } from '../../../../util/schedulers';
import { getActions } from '../../../../global';
import classNames from 'classnames';
import useLastCallback from '../../../../hooks/useLastCallback';

interface CustomInputProps {
  type?: string;
  label?: string;
  labelName?: string;
  iconLeft?: JSX.Element;
  iconRight?: JSX.Element;
  className: string;
  is_required?: boolean;
  withoutCleaning?: boolean;
  regexp?: RegExp;
  prefix?: string;
  autoFocus: boolean;
  errorText?: string;
  helpText?: string;
  isValidated?: boolean;
  size?: 'default' | 'smaller' | 'tiny';
}

const runDebouncedForCheckUsername = debounce((cb) => cb(), 250, false);

const Input: FC<CustomInputProps & FieldProps> = ({
  field,
  form: { errors, setFieldValue },
  type = 'text',
  autoFocus,
  label,
  labelName,
  iconLeft,
  iconRight,
  is_required,
  className,
  withoutCleaning,
  regexp,
  prefix = '',
  size = 'default',
  isValidated,
  errorText,
  helpText,
  ...props
}) => {
  const { checkUsernameInRegister, checkEmailInRegister } = getActions();
  const { value, name } = field;
  const handleClearValue = () => {
    setFieldValue(name, '');
  };
  const [error, setError] = useState<string>();
  const inputRef = useRef(null);
  const isCheckName = name === 'username' && isValidated;
  const isCheckEmail = name === 'email';

  const handleChange = useLastCallback((e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;

    const newValue = regexp
      ? val.replace(regexp, '')
      : prefix
      ? val.replace(prefix, '')
      : val;

    setFieldValue(name, newValue);

    if (isCheckName) {
      runDebouncedForCheckUsername(() => {
        if (value !== newValue) {
          checkUsernameInRegister({ username: newValue });
        }
      });
    }
    if (isCheckEmail && newValue.includes('@')) {
      runDebouncedForCheckUsername(() => {
        if (value !== newValue) {
          checkEmailInRegister({ email: newValue });
        }
      });
    }
  });

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      //@ts-ignore
      inputRef.current.focus();
    }
  }, [inputRef, autoFocus]);

  useEffect(() => {
    setError(
      errorText || (typeof errors[name] === 'string' ? errors[name] : '')
    );
  }, [errors, errorText]);

  return (
    <div
      className={classNames('input', {
        error,
      })}
    >
      <div className='input-label-name'>{labelName}</div>
      <div
        className={classNames('input-wrapper', { amount: name === 'amount' })}
      >
        {iconLeft && <span className='input-icon left'>{iconLeft}</span>}
        {name === 'amount' && <IconSvg name='dollar' w='24' h='24' />}
        <input
          ref={inputRef}
          type={type}
          {...field}
          onChange={handleChange}
          value={`${prefix}${value}`}
          {...props}
          className={classNames('form-control', className, size, {
            'input-item--left-icon': iconLeft,
            'as-disabled': !value,
          })}
          style={
            name === 'amount'
              ? { width: `${(value.length || 1) * 26 + 8}px` }
              : undefined
          }
        />
        {label && !value && (
          <label>
            {is_required && <span>* </span>}
            {label}
          </label>
        )}

        {(value && !withoutCleaning) || iconRight ? (
          <div className='input-icon right'>
            {value && !withoutCleaning && (
              <button type='button' onClick={handleClearValue}>
                <IconSvg name='close-circle' />
              </button>
            )}
            {iconRight}
          </div>
        ) : null}
      </div>
      {error && (
        <p
          className='input-notification input-notification--error'
          dangerouslySetInnerHTML={{ __html: String(error) }}
        />
      )}
      {helpText && <p className='input-notification'>{helpText}</p>}
    </div>
  );
};

export default Input;
